import { getChatApiRoute } from '@renderer/chat/api/v1Client'

export type SkillCompareSide = 'old' | 'new'
export type SkillComparePick = SkillCompareSide | 'tie'

/** A candidate saved on skills.hlai.dev; the app compares the active one per skill and field. */
export interface SkillCandidateListing {
  id: string
  skillSlug: string
  skillExternalId: string
  field: 'prompt' | 'setup'
}

export interface SkillComparisonSide {
  jobId: string | null
  jobStatus: string | null
  conversationId: string | null
  conversationIds: Array<string>
  stagedActionCount: number
}

export interface SkillComparisonPair {
  id: string
  audioNoteId: string | null
  leftIsNew: boolean
  error: string | null
  pick: SkillComparePick | null
  resolvedBy: 'pick' | null
  old: SkillComparisonSide
  new: SkillComparisonSide
}

export interface SkillComparisonTally {
  old: number
  new: number
  tie: number
  timeout: number
  pending: number
  failed: number
}

export interface SkillComparison {
  id: string
  skillSlug: string
  mode: 'batch' | 'live'
  tally: SkillComparisonTally
}

export interface SkillComparisonDetail extends SkillComparison {
  pairs: Array<SkillComparisonPair>
}

/** What the gateway stamps on a conversation it opened as one side of a pair (a scheduled fire). */
export interface ConversationCompareStamp {
  pairId: string
  comparisonId: string
  skillSlug: string
  leftIsNew: boolean
  side: SkillCompareSide
}

type GetAuthToken = () => Promise<string>

/** A refused request; `status` tells a 403 (not allowed) apart from a blip. */
export class SkillCompareRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

async function requestJson<T>(path: string, getAuthToken: GetAuthToken, body?: unknown): Promise<T> {
  const token = await getAuthToken()
  const response = await fetch(getChatApiRoute(`api/v1/${path}`), {
    method: body ? 'POST' : 'GET',
    headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
  const json = (await response.json().catch(() => null)) as { data?: T; error?: unknown; message?: unknown } | null
  if (!response.ok) {
    const message =
      typeof json?.error === 'string' ? json.error : typeof json?.message === 'string' ? json.message : null
    throw new SkillCompareRequestError(message ?? `Skill compare request failed (${response.status}).`, response.status)
  }
  if (!json) throw new Error('Skill compare request returned no body.')
  return json.data as T
}

/** The active candidates, one per skill and field. Admin only; a non-admin gets a 403. */
export function listSkillCandidates(getAuthToken: GetAuthToken): Promise<Array<SkillCandidateListing>> {
  return requestJson('skill-comparisons/candidates', getAuthToken)
}

export function createLiveComparison(
  candidate: SkillCandidateListing,
  getAuthToken: GetAuthToken,
): Promise<SkillComparisonDetail> {
  return requestJson('skill-comparisons/live', getAuthToken, { slug: candidate.skillSlug, candidateId: candidate.id })
}

/** Replay the caller's recent meetings once per side, in a sandbox. */
export function createBatchComparison(
  candidate: SkillCandidateListing,
  getAuthToken: GetAuthToken,
): Promise<SkillComparisonDetail> {
  return requestJson('skill-comparisons', getAuthToken, { slug: candidate.skillSlug, candidateId: candidate.id })
}

export function listSkillComparisons(slug: string, getAuthToken: GetAuthToken): Promise<Array<SkillComparison>> {
  return requestJson(`skill-comparisons?slug=${encodeURIComponent(slug)}&limit=10`, getAuthToken)
}

export function getSkillComparison(id: string, getAuthToken: GetAuthToken): Promise<SkillComparisonDetail> {
  return requestJson(`skill-comparisons/${encodeURIComponent(id)}`, getAuthToken)
}

/** The pair a conversation runs a side of, or null for an ordinary chat. */
export async function getConversationCompareStamp(
  conversationId: string,
  getAuthToken: GetAuthToken,
): Promise<ConversationCompareStamp | null> {
  const conversation = await requestJson<{ metadata?: { skillCompare?: ConversationCompareStamp } | null }>(
    `conversations/${encodeURIComponent(conversationId)}`,
    getAuthToken,
  )
  return conversation.metadata?.skillCompare ?? null
}

export function pickSkillComparisonPair(
  pairId: string,
  pick: SkillComparePick,
  getAuthToken: GetAuthToken,
): Promise<SkillComparisonPair> {
  return requestJson(`skill-comparisons/pairs/${encodeURIComponent(pairId)}/pick`, getAuthToken, { pick })
}
